import React, { useEffect, useState, useCallback } from "react";
import TemplateApi from "../../api/TemplateApi";
import AddDetails from "./AddDetails";
import EditDetails from "./EditDetails";
import { showError, showSuccess } from "../../utils/toastUtils";
import Auth from "../../utils/AuthUtils";
import { can } from "../../utils/permissionUtils";

function List() {
  const [templates, setTemplates] = useState([]);
  const [editData, setEditData] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Selling Logic States
  const [sellTemplateId, setSellTemplateId] = useState(null);
  const [clientEmail, setClientEmail] = useState("");
  const [sellLoading, setSellLoading] = useState(false);

  const user = Auth.getUserDetails();
  const permissions = user?.permissions;
  const role = user?.role?.toLowerCase();

  /* ================= FETCH DATA ================= */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await TemplateApi.list();
      if (res.code === 200) setTemplates(res.data);
    } catch {
      showError("Failed to load inventory templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ================= BRIDGE: EXPORT TO SELL ================= */
  const handleExportToSell = async (e) => {
    e.preventDefault();
    if (!clientEmail.trim()) return showError("Please enter client email");

    try {
      setSellLoading(true);
      const res = await TemplateApi.exportToSell({
        templateId: sellTemplateId,
        clientEmail: clientEmail.toLowerCase(),
      });

      if (res.code === 200) {
        showSuccess(res.message);
        setClientEmail("");
        loadData();
        const modalElement = document.getElementById("sellModal");
        const modalInstance =
          window.bootstrap?.Modal?.getInstance(modalElement) ||
          new window.bootstrap.Modal(modalElement);
        modalInstance.hide();
      }
    } catch (err) {
      showError(err.response?.data?.message || "Export failed");
    } finally {
      setSellLoading(false);
    }
  };

  const handleRemoveFromSell = async (templateId) => {
    try {
      const res = await TemplateApi.removeFromSell({ templateId });
      if (res.code === 200) {
        showSuccess("Template successfully returned to inventory");
        loadData();
      }
    } catch {
      showError("Failed to move back");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const res = await TemplateApi.remove({ id: deleteId });
      if (res.code === 200) {
        showSuccess(res.message);
        loadData();
        const modalElement = document.getElementById("delete-template-modal");
        const modalInstance =
          window.bootstrap?.Modal?.getInstance(modalElement) ||
          new window.bootstrap.Modal(modalElement);
        modalInstance.hide();
      }
    } catch {
      showError("Delete failed");
    }
  };

  const filteredtemplate = templates.filter((item) => {
    const search = searchTerm.toLowerCase().trim();

    return item.templateName?.toLowerCase().includes(search);
  });

  return (
    <div className="content pb-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold">
          Templates{" "}
          <span className="badge badge-soft-primary ms-2">
            {filteredtemplate.length}
          </span>
        </h4>
      </div>

      <div className="d-flex justify-content-between align-items-center justify-content-between mb-4">
        <div className="group-search-box position-relative">
          <i className="ti ti-search search-icon"></i>

          <input
            type="text"
            className="form-control search-input"
            placeholder="Search by Template..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {can(permissions, "write", "template") && role !== "client" && (
          <button
            className="btn btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#add-template-modal"
          >
            <i className="ti ti-plus me-1" /> Add Template
          </button>
        )}
      </div>

      <div className="row">
        {loading ? (
          <div className="col-12 text-center p-5">
            <div className="spinner-border text-primary"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="col-12 text-center p-5 text-muted">
            No templates found in inventory.
          </div>
        ) : (
          filteredtemplate.map((t) => (
            <div className="col-md-4 mb-4" key={t._id}>
              <div
                className={`card h-100 border-0 shadow-sm overflow-hidden ${t.status === "Sold" ? "opacity-75" : ""}`}
              >
                {/* ✅ WEBSITE PREVIEW (IFRAME) INSTEAD OF IMAGE */}
                <div
                  className="card-img-top bg-light position-relative"
                  style={{ height: "200px", overflow: "hidden" }}
                >
                  <iframe
                    src={t.url}
                    title={t.templateName}
                    width="100%"
                    height="100%"
                    style={{
                      border: "none",
                      pointerEvents: "none", // Card click prevent karne ke liye
                      zoom: "0.5", // Scale preview to fit
                      WebkitTransform: "scale(1)",
                      transformOrigin: "0 0",
                    }}
                    sandbox="allow-scripts allow-same-origin"
                    loading="lazy"
                  ></iframe>

                  {/* Overlay to prevent interaction inside card preview */}
                  <div className="position-absolute top-0 start-0 w-100 h-100 bg-transparent"></div>

                  {t.status === "Sold" && (
                    <span className="position-absolute top-0 start-0 m-2 badge bg-danger shadow-sm">
                      SOLD
                    </span>
                  )}
                </div>

                <div className="card-body d-flex flex-column">
                  <h6 className="fw-bold text-dark text-truncate mb-1">
                    {t.templateName}
                  </h6>
                  <p className="small text-muted text-truncate-2 mb-3 flex-grow-1">
                    {t.description || "No description provided."}
                  </p>

                  <div className="d-flex gap-1 pt-2 border-top">
                    <a
                      href={t.url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-xs btn-soft-info flex-grow-1"
                    >
                      <i className="ti ti-external-link me-1"></i>Live Preview
                    </a>

                    {t.status === "Available" ? (
                      <>
                        {can(permissions, "write", "template") && (
                          <button
                            className="btn btn-xs btn-outline-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#edit-template-modal"
                            onClick={() => setEditData(t)}
                            title="Edit Details"
                          >
                            <i className="ti ti-edit" />
                          </button>
                        )}
                        {can(permissions, "write", "Sell Template") && (
                          <button
                            className="btn btn-xs btn-success"
                            data-bs-toggle="modal"
                            data-bs-target="#sellModal"
                            onClick={() => setSellTemplateId(t._id)}
                            title="Sell to Client"
                          >
                            <i className="ti ti-shopping-cart" />
                          </button>
                        )}
                      </>
                    ) : (
                      can(permissions, "delete", "Sell Template") && (
                        <button
                          className="btn btn-xs btn-warning flex-grow-1"
                          onClick={() => handleRemoveFromSell(t._id)}
                        >
                          <i className="ti ti-refresh me-1" /> Restore
                        </button>
                      )
                    )}

                    {can(permissions, "delete", "template") && (
                      <button
                        className="btn btn-xs btn-outline-danger"
                        data-bs-toggle="modal"
                        data-bs-target="#delete-template-modal"
                        onClick={() => setDeleteId(t._id)}
                        title="Permanent Delete"
                      >
                        <i className="ti ti-trash" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* MODALS Remain Same */}
      <AddDetails refresh={loadData} />
      <EditDetails data={editData} refresh={loadData} />

      {/* SELL MODAL */}
      <div
        className="modal fade"
        id="sellModal"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header border-bottom-0">
              <h5 className="fw-bold">Assign Template to Client</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>
            <form onSubmit={handleExportToSell}>
              <div className="modal-body">
                <p className="small text-muted mb-3">
                  Enter the registered email of the client to complete the sale.
                </p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Registered Email Address of Client
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="example@client.com"
                    name="clientEmail"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={sellLoading}
                >
                  {sellLoading ? (
                    <span className="spinner-border spinner-border-sm me-1"></span>
                  ) : (
                    "Confirm Sale"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* DELETE CONFIRMATION */}
      <div className="modal fade" id="delete-template-modal" tabIndex={-1}>
        <div className="modal-dialog modal-sm modal-dialog-centered">
          <div className="modal-content border-0 shadow">
            <div className="modal-body text-center p-4">
              <div className="text-danger mb-3">
                <i className="ti ti-alert-triangle fs-1"></i>
              </div>
              <p className="fw-bold mb-1">Are you sure?</p>
              <p className="small text-muted">
                This template will be removed from inventory.
              </p>
              <div className="d-flex gap-2 mt-3">
                <button className="btn btn-light w-100" data-bs-dismiss="modal">
                  Cancel
                </button>
                <button
                  className="btn btn-danger w-100"
                  onClick={handleDeleteConfirm}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;
